import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button as TextButton
} from '@mui/material'
import {
  fetchVaultRemoveCheck,
  removeVault,
  vaultRemoveRefusal,
  VaultRemoveBlocker
} from 'api/inventory'
import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import { kidVaultPath } from 'components/Routes'
import { useClientConfig } from 'hooks/useClientConfig'
import { openMidasPanelWithDraft, useMidasEnabled } from 'hooks/useMidas'
import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

// Removing an empty Kids Vault (Greg, 10 Oct 2026; option B of
// docs/kids-vaults/EXPLORATION.md section 1), the app's flow on the website,
// behind kids_vault_self_remove on GET /config. "Remove this Kids Vault" asks
// goldie-cloud first (GET /vaults/{id}/remove-check):
// - nothing in the way: "Mia's vault is empty. Remove it?", then POST
//   /vaults/{id}/remove, and the customer is back on the Kids vault list. The
//   backend keeps every record and emails the account holder;
// - something in the way: the backend's own words ("This vault holds 2.345 g
//   of gold. Sell it first, or contact us.") and Contact us: Midas with the
//   request typed, or an email to support while Midas is off.
// A removal refused because something changed since the check (409) shows what
// is in the way now; a vault already gone (404) counts as removed.

export const REMOVE_COPY = {
  link: 'Remove this Kids Vault',
  checking: 'Checking this vault',
  confirmBody:
    "It will no longer show on the website or in the app. We'll email you to confirm.",
  remove: 'Remove vault',
  keep: 'Keep it',
  blockedTitle: "This vault can't be removed yet",
  blockedGeneric: 'Something in this vault still needs to finish first.',
  contactUs: 'Contact us',
  close: 'Close',
  errorTitle: "We couldn't check this vault",
  errorBody: 'Please try again.',
  tryAgain: 'Try again',
  removeFailed: "We couldn't remove this vault. Please try again.",
  removed: 'Kids Vault removed'
}

const SUPPORT_EMAIL = 'support@getgoldie.co.nz'

/** "Mia's vault is empty. Remove it?" */
export const removeQuestion = (name: string): string =>
  name.trim()
    ? `${name.trim()}'s vault is empty. Remove it?`
    : 'This Kids Vault is empty. Remove it?'

/** What Contact us types for the customer, naming the vault as support finds it. */
export const removalRequest = (name: string): string =>
  name.trim()
    ? `I'd like to remove my Kids Vault '${name.trim()}'.`
    : "I'd like to remove one of my Kids Vaults."

type View =
  | { kind: 'checking' }
  | { kind: 'error' }
  | { kind: 'blocked'; blockers: VaultRemoveBlocker[] }
  | { kind: 'confirm'; failed: boolean }

export default function RemoveKidVault({
  vaultId,
  vaultName
}: Readonly<{ vaultId: number; vaultName: string }>): JSX.Element | null {
  const { data: clientConfig } = useClientConfig()
  const { enabled: midasEnabled } = useMidasEnabled()
  const { refreshCurrentUser, setToast } = useAuth()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [view, setView] = useState<View>({ kind: 'checking' })
  const [removing, setRemoving] = useState(false)
  // Only the latest check may set the view: closing and opening again starts a new one.
  const run = useRef(0)

  if (clientConfig?.features?.kids_vault_self_remove !== true) return null

  const removed = async () => {
    setOpen(false)
    // The vault list comes from /user/me: read it again so the list no longer has it.
    await refreshCurrentUser().catch(() => undefined)
    setToast({
      message: REMOVE_COPY.removed,
      open: true,
      duration: 3000,
      type: 'success'
    })
    navigate(kidVaultPath())
  }

  const check = async () => {
    const mine = ++run.current
    setView({ kind: 'checking' })
    try {
      const result = await fetchVaultRemoveCheck(vaultId)
      if (run.current !== mine) return
      setView(
        result.eligible
          ? { kind: 'confirm', failed: false }
          : { kind: 'blocked', blockers: result.blockers }
      )
    } catch (err) {
      if (run.current !== mine) return
      // Already removed (in the app, or by support): nothing is left to remove.
      if (vaultRemoveRefusal(err).kind === 'gone') {
        await removed()
        return
      }
      setView({ kind: 'error' })
    }
  }

  const start = () => {
    setOpen(true)
    void check()
  }

  const close = () => {
    run.current++
    setOpen(false)
  }

  const onRemove = async () => {
    setRemoving(true)
    try {
      await removeVault(vaultId)
    } catch (err) {
      setRemoving(false)
      const refusal = vaultRemoveRefusal(err)
      if (refusal.kind === 'gone') await removed()
      else if (refusal.kind === 'blocked')
        setView({ kind: 'blocked', blockers: refusal.blockers })
      else setView({ kind: 'confirm', failed: true })
      return
    }
    setRemoving(false)
    await removed()
  }

  const contactUs = () => {
    setOpen(false)
    openMidasPanelWithDraft(removalRequest(vaultName))
  }

  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    'Remove a Kids Vault'
  )}&body=${encodeURIComponent(removalRequest(vaultName))}`

  return (
    <>
      <Box mt={3} textAlign='center'>
        <TextButton
          variant='text'
          color='primary'
          onClick={start}
          sx={{ textTransform: 'none', textDecoration: 'underline' }}
        >
          {REMOVE_COPY.link}
        </TextButton>
      </Box>

      <Dialog
        open={open}
        onClose={removing ? undefined : close}
        aria-labelledby='remove-kid-vault-title'
      >
        {view.kind === 'checking' && (
          <DialogContent>
            <Box display='flex' justifyContent='center' p={3}>
              <CircularProgress aria-label={REMOVE_COPY.checking} />
            </Box>
          </DialogContent>
        )}

        {view.kind === 'error' && (
          <>
            <DialogTitle id='remove-kid-vault-title'>
              {REMOVE_COPY.errorTitle}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>{REMOVE_COPY.errorBody}</DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' onClick={close}>
                {REMOVE_COPY.close}
              </Button>
              <Button variant='contained' onClick={() => void check()}>
                {REMOVE_COPY.tryAgain}
              </Button>
            </DialogActions>
          </>
        )}

        {view.kind === 'blocked' && (
          <>
            <DialogTitle id='remove-kid-vault-title'>
              {REMOVE_COPY.blockedTitle}
            </DialogTitle>
            <DialogContent>
              {(view.blockers.length
                ? view.blockers.map(b => b.message)
                : [REMOVE_COPY.blockedGeneric]
              ).map(message => (
                <Alert key={message} severity='info' sx={{ mb: 1 }}>
                  {message}
                </Alert>
              ))}
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' onClick={close}>
                {REMOVE_COPY.close}
              </Button>
              {midasEnabled ? (
                <Button variant='contained' onClick={contactUs}>
                  {REMOVE_COPY.contactUs}
                </Button>
              ) : (
                <Button variant='contained' href={mailto}>
                  {REMOVE_COPY.contactUs}
                </Button>
              )}
            </DialogActions>
          </>
        )}

        {view.kind === 'confirm' && (
          <>
            <DialogTitle id='remove-kid-vault-title'>
              {removeQuestion(vaultName)}
            </DialogTitle>
            <DialogContent>
              <DialogContentText>{REMOVE_COPY.confirmBody}</DialogContentText>
              {view.failed && (
                <Alert severity='error' sx={{ mt: 2 }}>
                  {REMOVE_COPY.removeFailed}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button variant='outlined' onClick={close} disabled={removing}>
                {REMOVE_COPY.keep}
              </Button>
              <Button
                variant='contained'
                color='primary'
                onClick={() => void onRemove()}
                disabled={removing}
                loading={removing}
              >
                {REMOVE_COPY.remove}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}
